/**
 * Decoded JWT payload with standard claims.
 */
export interface JwtPayload {
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

/**
 * Decodes a JWT token and returns the payload.
 * Returns null if the token is not a valid JWT.
 */
export function decodeJwtPayload(token: string | undefined | null): JwtPayload | null {
  if (!token) {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) {
      return null;
    }

    // base64url → base64: replace URL-safe chars and add padding
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) {
      b64 += '=';
    }

    return JSON.parse(atob(b64)) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Keycloak connection details derived from an access token.
 */
export interface KeycloakConfigFromToken {
  url: string;
  realm: string;
  clientId: string;
}

const REALM_PATH_SEGMENT = '/realms/';

/**
 * Derives the Keycloak connection details from an access token, so a caller
 * only has to hand us the token itself. The issuer holds the auth base URL and
 * the realm ("https://sso.example.com/auth/realms/my-realm"), the authorized
 * party (azp) holds the client id.
 *
 * Returns null when the token is not decodable or carries no issuer/azp.
 */
export function getKeycloakConfigFromToken(
  token: string | undefined | null
): KeycloakConfigFromToken | null {
  const payload = decodeJwtPayload(token);
  const issuer = typeof payload?.iss === 'string' ? payload.iss : null;
  const clientId = typeof payload?.azp === 'string' ? payload.azp : null;

  if (!issuer || !clientId) {
    return null;
  }

  const realmSegmentIndex = issuer.lastIndexOf(REALM_PATH_SEGMENT);
  if (realmSegmentIndex === -1) {
    return null;
  }

  const url = issuer.slice(0, realmSegmentIndex);
  const realm = issuer.slice(realmSegmentIndex + REALM_PATH_SEGMENT.length);

  if (!url || !realm) {
    return null;
  }

  return { url, realm, clientId };
}

/**
 * Returns the expiration date from a JWT token, or null if not available.
 */
export function getJwtExpiration(token: string | undefined | null): Date | null {
  const payload = decodeJwtPayload(token);
  if (payload?.exp) {
    return new Date(payload.exp * 1000);
  }
  return null;
}

/**
 * Returns whether a JWT token is expired (optionally with a buffer in seconds).
 */
export function isJwtExpired(token: string | undefined | null, bufferSeconds = 0): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return false; // No expiry info — treat as valid
  }
  return payload.exp * 1000 - Date.now() <= bufferSeconds * 1000;
}
